import { Box, ToggleButton, ToggleButtonGroup, Tab, Tabs } from '@mui/material';
import Header from '../../components/Header';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterContext } from '../../contexts/FilterContext';
import moment from 'moment';
import UserStatisticsTab from './UserStatisticsTab';
import CampaignStatisticsTab from './CampaignStatisticsTab';

interface IterableStatisticsProps {
  defaultStartDate?: Date;
}

const TAB_NAMES = ['users', 'campaigns'] as const;
type TabName = (typeof TAB_NAMES)[number];

const IterableStatistics: React.FC<IterableStatisticsProps> = ({ defaultStartDate }) => {
  const { filterState, setFilterState } = useContext(FilterContext)!;
  const [searchParams, setSearchParams] = useSearchParams();
  const [aggregation, setAggregation] = useState<string>('daily');

  // Tab state is URL-derived for shareable links
  const tabParam = (searchParams.get('tab') || 'users') as TabName;
  const activeTab = TAB_NAMES.indexOf(tabParam) >= 0 ? TAB_NAMES.indexOf(tabParam) : 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', TAB_NAMES[newValue]);
    setSearchParams(params);
  };

  // Override the app-wide FilterContext default (14 days) with a cohort-
  // appropriate start date on mount — BUT only if the URL didn't already
  // carry a startDate (preserves shareable links). Functional update keeps
  // other filter keys intact (per CLAUDE.md "stale closure fix" guidance).
  useEffect(() => {
    if (!defaultStartDate) return;
    if (searchParams.get('startDate')) return;
    setFilterState((prev) => ({
      ...prev,
      startDate: defaultStartDate,
    }));
    // eslint-disable-next-line
  }, [defaultStartDate]);

  const startDate = useMemo(
    () => moment(filterState.startDate).format('YYYY-MM-DD'),
    [filterState.startDate],
  );
  const endDate = useMemo(
    () => moment(filterState.endDate).format('YYYY-MM-DD'),
    [filterState.endDate],
  );

  // Auto-switch aggregation only when date range changes
  useEffect(() => {
    const daysDiff = moment(filterState.endDate).diff(moment(filterState.startDate), 'days');

    if (daysDiff > 120) {
      setAggregation('monthly');
    } else if (daysDiff >= 45) {
      setAggregation('weekly');
    } else {
      setAggregation('daily');
    }
    // eslint-disable-next-line
  }, [filterState.startDate, filterState.endDate]);

  const handleAggregationChange = (
    event: React.MouseEvent<HTMLElement>,
    newAggregation: string | null,
  ) => {
    if (newAggregation !== null) {
      setAggregation(newAggregation);
    }
  };

  return (
    <Box m="20px">
      <Box justifyContent="space-between" alignItems="center" display="flex" mb={2}>
        <Header
          title="Iterable Statistiken"
          subtitle="Kampagnen-Performance & Nutzer-Entwicklung"
        />
      </Box>

      {/* Tabs */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Nutzer & Liste" />
          <Tab label="Kampagnen & Versand" />
        </Tabs>

        <ToggleButtonGroup
          value={aggregation}
          exclusive
          onChange={handleAggregationChange}
          size="small"
        >
          <ToggleButton value="daily">Täglich</ToggleButton>
          <ToggleButton value="weekly">Wöchentlich</ToggleButton>
          <ToggleButton value="monthly">Monatlich</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <UserStatisticsTab startDate={startDate} endDate={endDate} aggregation={aggregation} />
      )}
      {activeTab === 1 && (
        <CampaignStatisticsTab startDate={startDate} endDate={endDate} aggregation={aggregation} />
      )}
    </Box>
  );
};

export default IterableStatistics;
